import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProducts } from "@/hooks/useProducts";
import { uploadFile } from "@/services/supabaseService";
import type { Product } from "@/mocks/products";
import AdminLayout from "@/components/feature/AdminLayout";

export default function ProductManage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { products, addProduct, updateProduct, toggleProductStatus, removeProduct } = useProducts();
  const [search, setSearch] = useState("");
  const [showEditModal, setShowEditModal] = useState<Product | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState<string>("");
  const [confirmDelete, setConfirmDelete] = useState<Product | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button onClick={() => navigate("/")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">返回首頁</button>
        </div>
      </div>
    );
  }

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.includes(search) ||
    p.region.includes(search)
  );

  const handleSave = async (product: Product) => {
    try {
      await updateProduct(product);
      setShowEditModal(null);
      showToast("商品更新成功！");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "更新失敗";
      showToast("更新失敗：" + msg);
    }
  };

  const handleCreate = async (product: Product) => {
    try {
      await addProduct(product);
      setShowCreateModal(false);
      showToast("商品新增成功！");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "新增失敗";
      showToast("新增失敗：" + msg);
    }
  };

  const handleDelete = async (product: Product) => {
    try {
      await removeProduct(product.id);
      setConfirmDelete(null);
      showToast("商品已永久刪除！");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "刪除失敗";
      showToast("刪除失敗：" + msg);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">商品庫存管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">管理商品成本價、認購價、銷售價及庫存</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 cursor-pointer flex items-center gap-2 whitespace-nowrap shadow-sm"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm"></i>
            </div>
            新增商品
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium flex items-center gap-2 shadow-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-information-line text-amber-600"></i>
            </div>
            {toast}
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-sm text-gray-400"></i>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋商品名稱或品類..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm bg-white border border-gray-200 outline-none focus:border-amber-400 shadow-sm"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">商品</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">所屬大區</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">小分類</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">認購價</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">銷售價</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">庫存</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((product) => {
                  const isActive = product.stock > 0;
                  return (
                    <tr key={product.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={product.images[0]} alt={product.name} className="w-10 h-10 rounded object-cover" />
                          <p className="font-medium text-gray-800 line-clamp-1">{product.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{product.region}</td>
                      <td className="px-4 py-3 text-gray-500">{product.category}</td>
                      <td className="px-4 py-3 font-medium text-amber-600">${product.distributionPrice}</td>
                      <td className="px-4 py-3 text-gray-700">${product.suggestedPrice}</td>
                      <td className="px-4 py-3 text-gray-700">{isActive ? product.stock : "下架"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                        }`}>
                          {isActive ? "上架中" : "已下架"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowEditModal(product)}
                            className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-amber-100 hover:text-amber-600 cursor-pointer"
                          >
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-edit-line text-xs"></i>
                            </div>
                          </button>
                          <button
                            onClick={() => toggleProductStatus(product.id)}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer ${
                              isActive
                                ? "bg-red-50 text-red-500 hover:bg-red-100"
                                : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                            }`}
                          >
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className={`${isActive ? "ri-eye-off-line" : "ri-eye-line"} text-xs`}></i>
                            </div>
                          </button>
                          <button
                            onClick={() => setConfirmDelete(product)}
                            className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-100 cursor-pointer"
                          >
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-delete-bin-line text-xs"></i>
                            </div>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">未找到商品</div>
          )}
        </div>
      </div>

      {/* Confirm Delete Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-500">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-delete-bin-line text-lg"></i>
                </div>
              </div>
              <h3 className="text-base font-bold text-gray-900">確認刪除商品</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              確定要永久刪除「<span className="font-medium text-gray-800">{confirmDelete.name}</span>」嗎？此操作無法復原。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 cursor-pointer"
              >
                確定刪除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {(showEditModal || showCreateModal) && (
        <ProductEditModal
          product={showEditModal}
          onSave={showEditModal ? handleSave : handleCreate}
          onClose={() => { setShowEditModal(null); setShowCreateModal(false); }}
        />
      )}
    </AdminLayout>
  );
}

/* ===== Product Edit/Create Modal ===== */

function ProductEditModal({
  product,
  onSave,
  onClose,
}: {
  product: Product | null;
  onSave: (p: Product) => void;
  onClose: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageMode, setImageMode] = useState<"url" | "upload">("url");
  const [imageUrlInput, setImageUrlInput] = useState(product?.images[0] || "");
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // === Text fields ===
  const [name, setName] = useState(product?.name || "");
  const [region, setRegion] = useState(product?.region || "全部商品");
  const [category, setCategory] = useState(product?.category || "休閒");
  const [description, setDescription] = useState(product?.description || "");
  const [tagsStr, setTagsStr] = useState(
    Array.isArray(product?.tags) ? product.tags.join(", ") : ""
  );

  // === Number fields ===
  const [stockStr, setStockStr] = useState(String(product?.stock ?? ""));
  const [distStr, setDistStr] = useState(String(product?.distributionPrice ?? ""));
  const [suggestStr, setSuggestStr] = useState(String(product?.suggestedPrice ?? ""));

  // === Toggles ===
  const [isHot, setIsHot] = useState(product?.isHot ?? false);
  const [isNew, setIsNew] = useState(product?.isNew ?? true);
  const [isOnSale, setIsOnSale] = useState(product?.isOnSale ?? false);

  const currentImage = uploadedImage || imageUrlInput || "";

  const regionOptions = ["全部商品", "體驗認購區", "VIP認購區", "吉時好物"];

  const handleNumFocus = (setter: (v: string) => void, currentVal: string) =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (currentVal === "0" || currentVal === "0.0" || currentVal === "0.00") {
        setter("");
      }
      e.target.select();
    };

  const handleNumChange = (setter: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
    };

  const handleNumBlur = (setter: (v: string) => void) =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      let val = e.target.value;
      if (val === "" || val === "." || val === "-") {
        setter("0");
        return;
      }
      if (val.length > 1 && val.startsWith("0") && !val.startsWith("0.")) {
        val = val.replace(/^0+/, "") || "0";
        setter(val);
      }
    };

  const distNum = parseFloat(distStr) || 0;
  const suggestNum = parseFloat(suggestStr) || 0;
  const distributorProfit = suggestNum - distNum;
  const distributorProfitRate = suggestNum > 0
    ? Math.round((distributorProfit / suggestNum) * 100)
    : 0;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setUploadError("圖片大小不可超過 2MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setUploadError("僅支援圖片格式");
      return;
    }

    setUploadError("");

    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `products/${Date.now()}_${Math.random().toString(36).slice(2, 6)}.${ext}`;
      const { url } = await uploadFile(file, "products", path);
      setUploadedImage(url);
      setImageUrlInput("");
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : "圖片上傳失敗");
    }
  };

  const handleUrlChange = (url: string) => {
    setImageUrlInput(url);
    setUploadedImage(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const finalImage =
        uploadedImage ||
        imageUrlInput ||
        "https://readdy.ai/api/search-image?query=generic%20product%20placeholder%20white%20background%20minimal%20clean%20photography&width=400&height=400&seq=newprod&orientation=squarish";

      const newProduct: Product = {
        id: product?.id || "",
        name,
        region,
        category,
        costPrice: product?.costPrice ?? 0,
        distributionPrice: parseFloat(distStr) || 0,
        normalPrice: product?.normalPrice ?? 0,
        corePrice: product?.corePrice ?? 0,
        dealerPrice: product?.dealerPrice ?? 0,
        suggestedPrice: parseFloat(suggestStr) || 0,
        stock: parseInt(stockStr, 10) || 0,
        images: [finalImage],
        description,
        isHot,
        isNew,
        isOnSale,
        tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
      };

      await onSave(newProduct);
    } catch {
      // Error toast handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          {product ? "編輯商品" : "新增商品"}
        </h3>
        <div className="space-y-4">

          {/* ===== Image Section ===== */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="text-sm font-medium text-gray-700 mb-3 block">商品圖片</label>

            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setImageMode("url")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                  imageMode === "url"
                    ? "bg-amber-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-link text-xs"></i>
                  </div>
                  圖片網址
                </div>
              </button>
              <button
                onClick={() => setImageMode("upload")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                  imageMode === "upload"
                    ? "bg-amber-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-upload-cloud-line text-xs"></i>
                  </div>
                  上傳圖片
                </div>
              </button>
            </div>

            {imageMode === "url" && (
              <div>
                <input
                  type="text"
                  value={imageUrlInput}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  placeholder="貼上圖片網址（https://...）"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                />
              </div>
            )}

            {imageMode === "upload" && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-amber-400 hover:bg-amber-50/50 transition-colors cursor-pointer flex flex-col items-center gap-1"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <i className="ri-upload-cloud-line text-sm"></i>
                  </div>
                  <span className="text-xs text-gray-500">點擊選擇圖片（最大 2MB）</span>
                </button>
                {uploadError && (
                  <p className="text-xs text-red-500 mt-2">{uploadError}</p>
                )}
              </div>
            )}

            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-2">圖片預覽</p>
              {currentImage ? (
                <div className="relative">
                  <img
                    src={currentImage}
                    alt="商品預覽"
                    className="w-full h-48 object-cover rounded-lg border border-gray-100"
                    onError={() => {
                      if (imageMode === "url" && imageUrlInput) {
                        setUploadError("圖片載入失敗，請檢查網址是否正確");
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      setUploadedImage(null);
                      setImageUrlInput("");
                      setUploadError("");
                    }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 cursor-pointer"
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-close-line text-xs"></i>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="w-full h-48 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 bg-gray-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 text-gray-400">
                    <i className="ri-image-line text-lg"></i>
                  </div>
                  <p className="text-xs text-gray-400">尚未設定圖片</p>
                </div>
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-sm text-gray-600 mb-1.5 block">商品名稱</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
            />
          </div>

          {/* Region + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">所屬大區 <span className="text-red-500">*</span></label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 appearance-none bg-white cursor-pointer"
              >
                {regionOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">庫存</label>
              <input
                type="number"
                min={0}
                step={1}
                value={stockStr}
                onChange={handleNumChange(setStockStr)}
                onFocus={handleNumFocus(setStockStr, stockStr)}
                onBlur={handleNumBlur(setStockStr)}
                placeholder="0"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
          </div>

          {/* Detailed category */}
          <div>
            <label className="text-sm text-gray-600 mb-1.5 block">詳細小分類</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="例如：休閒、寵物、家電"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
            />
            <p className="text-xs text-gray-400 mt-1">輸入商品的細項標籤，例如：休閒、寵物、家電</p>
          </div>

          {/* Prices */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">認購價</label>
              <input
                type="number"
                min={0}
                step="any"
                value={distStr}
                onChange={handleNumChange(setDistStr)}
                onFocus={handleNumFocus(setDistStr, distStr)}
                onBlur={handleNumBlur(setDistStr)}
                placeholder="0"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">銷售價</label>
              <input
                type="number"
                min={0}
                step="any"
                value={suggestStr}
                onChange={handleNumChange(setSuggestStr)}
                onFocus={handleNumFocus(setSuggestStr, suggestStr)}
                onBlur={handleNumBlur(setSuggestStr)}
                placeholder="0"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-gray-600 mb-1.5 block">商品描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 resize-none"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="text-sm text-gray-600 mb-1.5 block">標籤（用逗號分隔）</label>
            <input
              type="text"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              placeholder="例如：日本進口, 補水保濕, 工廠直供"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
            />
          </div>

          {/* Checkboxes */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={isHot}
                onChange={(e) => setIsHot(e.target.checked)}
                className="rounded"
              />
              爆款
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={isNew}
                onChange={(e) => setIsNew(e.target.checked)}
                className="rounded"
              />
              新品
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={isOnSale}
                onChange={(e) => setIsOnSale(e.target.checked)}
                className="rounded"
              />
              促銷
            </label>
          </div>

          {/* Profit preview */}
          <div className="bg-emerald-50 rounded-lg p-3 text-sm">
            <p className="text-emerald-700 font-medium">
              分銷商利潤：${distributorProfit.toFixed(2)} ({distributorProfitRate}%)
            </p>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}